import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import adminService, { type AdminDeveloperDetail } from '../../../services/adminService';

export default function AdminDeveloperDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [dev, setDev] = useState<AdminDeveloperDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setLoading(true);
    adminService.getDeveloperDetail(id).then(setDev).catch(() => setDev(null)).finally(() => setLoading(false));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try { await fn(); load(); } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen bg-white p-6"><p className="text-sm text-gray-400">Loading…</p></div>;
  if (!dev) return <div className="min-h-screen bg-white p-6"><Link to="/admin/developers" className="text-sm text-[#003930]">← Developers</Link><p className="mt-4 text-gray-500">Developer not found.</p></div>;

  const owner = dev.owner ? `${[dev.owner.firstName, dev.owner.lastName].filter(Boolean).join(' ')} (${dev.owner.email})` : '-';

  return (
    <div className="min-h-screen bg-white p-6">
      <div className="mx-auto max-w-4xl">
        <Link to="/admin/developers" className="text-sm text-[#003930] hover:underline">← Developers</Link>

        <div className="mt-3 mb-4 flex items-center gap-3">
          {dev.logo_url ? <img src={dev.logo_url} alt="" className="h-14 w-14 rounded-xl object-cover" /> : <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-[var(--pv-color-surface-sunken)] text-xl text-[#003930]">{dev.name.charAt(0)}</div>}
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-[var(--pv-color-primary)]">{dev.name}{dev.is_verified && <i className="ri-verified-badge-fill text-[#004D40]" />}</h1>
            <a href={`/developers/${dev.slug}`} target="_blank" rel="noreferrer" className="text-sm text-gray-400 hover:underline">/developers/{dev.slug} ↗</a>
          </div>
        </div>

        {/* Control actions */}
        <div className="mb-4 flex flex-wrap gap-2">
          <button disabled={busy} onClick={() => act(() => adminService.verifyDeveloper(dev.id, !dev.is_verified))}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60 ${dev.is_verified ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
            {dev.is_verified ? 'Revoke verification' : 'Verify (FAL passed)'}
          </button>
          <button disabled={busy} onClick={() => act(() => adminService.updateDeveloperStatus(dev.id, !dev.is_active))}
            className={`rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60 ${dev.is_active ? 'bg-gray-600 hover:bg-gray-700' : 'bg-[#004D40] hover:bg-[#003930]'}`}>
            {dev.is_active ? 'Deactivate' : 'Activate'}
          </button>
        </div>

        {/* Stats */}
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            { l: 'Projects', v: dev.projects.length },
            { l: 'Active units', v: dev.active_listings },
            { l: 'Team', v: dev.team.length },
            { l: 'Rating', v: dev.total_reviews > 0 ? dev.avg_rating.toFixed(1) : '-' },
          ].map((s) => (
            <div key={s.l} className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="text-xs text-gray-400">{s.l}</div>
              <div className="text-xl font-semibold text-gray-900">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Info */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-5 text-sm">
          <h2 className="mb-3 font-semibold text-[var(--pv-color-primary)]">Details</h2>
          <dl className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div><dt className="text-gray-400">Owner</dt><dd className="text-gray-800">{owner}</dd></div>
            <div><dt className="text-gray-400">FAL license</dt><dd className="font-mono text-gray-800">{dev.license_number || '-'}</dd></div>
            <div><dt className="text-gray-400">HQ city</dt><dd className="text-gray-800">{dev.headquarters_city || '-'}</dd></div>
            <div><dt className="text-gray-400">Founded</dt><dd className="text-gray-800">{dev.founded_year || '-'}</dd></div>
            <div><dt className="text-gray-400">Phone</dt><dd className="text-gray-800" dir="ltr">{dev.phone || '-'}</dd></div>
            <div><dt className="text-gray-400">Email</dt><dd className="text-gray-800" dir="ltr">{dev.email || '-'}</dd></div>
          </dl>
        </div>

        {/* Licenses */}
        {dev.licenses.length > 0 && (
          <div className="mb-4 rounded-xl border border-gray-200 bg-white p-5 text-sm">
            <h2 className="mb-3 font-semibold text-[var(--pv-color-primary)]">FAL licenses</h2>
            <ul className="space-y-2">
              {dev.licenses.map((l) => (
                <li key={l.id} className="flex items-center justify-between">
                  <span className="font-mono text-gray-700">{l.licenseNumber}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] ${l.status === 'VALID' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{l.status}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Team */}
        <div className="mb-4 rounded-xl border border-gray-200 bg-white p-5 text-sm">
          <h2 className="mb-3 font-semibold text-[var(--pv-color-primary)]">Team ({dev.team.length})</h2>
          <ul className="space-y-2">
            {dev.team.map((m) => (
              <li key={m.id} className="flex items-center justify-between">
                <span className="text-gray-700">{[m.user.firstName, m.user.lastName].filter(Boolean).join(' ') || m.user.email} <span className="text-gray-400">· {m.user.email}</span></span>
                <span className="rounded-full bg-[var(--pv-color-surface-sunken)] px-2 py-0.5 text-[11px] text-[#003930]">{m.role}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Projects */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 text-sm">
          <h2 className="mb-3 font-semibold text-[var(--pv-color-primary)]">Projects ({dev.projects.length})</h2>
          <ul className="space-y-2">
            {dev.projects.map((p) => (
              <li key={p.id} className="flex items-center justify-between">
                <a href={`/developers/project/${p.slug}`} target="_blank" rel="noreferrer" className="text-gray-700 hover:underline">{p.name}</a>
                <span className="flex gap-1">
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">{p.status}</span>
                  {p.investmentStatus === 'OPEN_FOR_INVESTMENT' && <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] text-indigo-700">Open</span>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
